import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import axiosInstance from '../../services/axiosInstance';

interface QueueStatus {
  queued: number;
  running: number;
  concurrency: number;
  runningIds: string[];
}

interface Asset {
  _id: string;
  title: string;
  fileName: string;
  aiStatus: string;
  lastAiAt?: string;
  aiAttempts?: number;
}

interface QueueDetails {
  queue: QueueStatus;
  stuckAssets: Asset[];
  pendingAssets: Asset[];
}

const AiQueueManagement: React.FC = () => {
  const [details, setDetails] = useState<QueueDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Fetch queue details
  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get<QueueDetails>('/api/assets/ai/queue-details');
      setDetails(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error fetching queue details');
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh every 5 seconds
  useEffect(() => {
    fetchDetails();
    const interval = setInterval(fetchDetails, 5000);
    return () => clearInterval(interval);
  }, []);

  // Clear queue
  const handleClearQueue = async () => {
    if (!window.confirm('Är du säker på att du vill tömma kön? Pågående jobb kommer fortsätta.')) {
      return;
    }
    
    try {
      setActionLoading('clear');
      setError(null);
      const response = await axiosInstance.post('/api/assets/ai/clear-queue');
      setSuccess(response.data.message || 'Kön tömd');
      await fetchDetails();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error clearing queue');
    } finally {
      setActionLoading(null);
    }
  };

  // Reset stuck assets
  const handleResetStuck = async () => {
    if (!window.confirm('Återställ fastnade jobb och köa om dem?')) {
      return;
    }
    
    try {
      setActionLoading('reset');
      setError(null);
      const response = await axiosInstance.post('/api/assets/ai/reset-stuck');
      setSuccess(response.data.message || 'Fastnade jobb återställda');
      await fetchDetails();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error resetting stuck assets');
    } finally {
      setActionLoading(null);
    }
  };

  // Retry specific asset
  const handleRetryAsset = async (assetId: string) => {
    try {
      setActionLoading(`retry-${assetId}`);
      setError(null);
      await axiosInstance.post('/api/assets/ai/retry', { assetIds: [assetId] });
      setSuccess('Asset återköad');
      await fetchDetails();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error retrying asset');
    } finally {
      setActionLoading(null);
    }
  };

  // Enqueue all pending assets
  const handleEnqueuePending = async () => {
    if (!details?.pendingAssets || details.pendingAssets.length === 0) {
      return;
    }

    if (!window.confirm(`Starta AI-processning för ${details.pendingAssets.length} väntande produkter?`)) {
      return;
    }
    
    try {
      setActionLoading('enqueue-pending');
      setError(null);
      const assetIds = details.pendingAssets.map(a => a._id);
      await axiosInstance.post('/api/assets/ai/retry', { assetIds });
      setSuccess(`${assetIds.length} produkter köade för AI-processning`);
      await fetchDetails();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Error enqueueing pending assets');
    } finally {
      setActionLoading(null);
    }
  };

  // Format date
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#072f53' }}>
          AI-kö Hantering
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchDetails}
          disabled={loading}
        >
          Uppdatera
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Status Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                I KÖ
              </Typography>
              <Typography variant="h3" sx={{ color: '#1976d2', fontWeight: 600 }}>
                {details?.queue.queued || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                produkter väntar
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                KÖRS NU
              </Typography>
              <Typography variant="h3" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                {details?.queue.running || 0} / {details?.queue.concurrency || 2}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                aktiva jobb
              </Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom variant="body2">
                FASTNADE
              </Typography>
              <Typography variant="h3" sx={{ color: details?.stuckAssets && details.stuckAssets.length > 0 ? '#d32f2f' : '#757575', fontWeight: 600 }}>
                {details?.stuckAssets?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                behöver återställas
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#072f53' }}>
          Åtgärder
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={actionLoading === 'enqueue-pending' ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            onClick={handleEnqueuePending}
            disabled={!!actionLoading || !details?.pendingAssets || details.pendingAssets.length === 0}
          >
            Starta Väntande ({details?.pendingAssets?.length || 0})
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={actionLoading === 'reset' ? <CircularProgress size={16} /> : <RestartAltIcon />}
            onClick={handleResetStuck}
            disabled={!!actionLoading || !details?.stuckAssets || details.stuckAssets.length === 0}
          >
            Återställ Fastnade ({details?.stuckAssets?.length || 0})
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={actionLoading === 'clear' ? <CircularProgress size={16} /> : <ClearIcon />}
            onClick={handleClearQueue}
            disabled={!!actionLoading || !details?.queue || details.queue.queued === 0}
          >
            Töm Kö ({details?.queue?.queued || 0})
          </Button>
        </Box>
      </Paper>

      {/* Stuck Assets Table */}
      {details?.stuckAssets && details.stuckAssets.length > 0 && (
        <Paper sx={{ mb: 3 }}>
          <Box sx={{ p: 2, bgcolor: '#fff3e0' }}>
            <Typography variant="h6" sx={{ color: '#e65100' }}>
              ⚠️ Fastnade Jobb ({details.stuckAssets.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dessa produkter har status "processing" men körs inte längre. De behöver återställas.
            </Typography>
          </Box>
          <Divider />
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Titel</strong></TableCell>
                  <TableCell><strong>Filnamn</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Senaste AI</strong></TableCell>
                  <TableCell align="center"><strong>Försök</strong></TableCell>
                  <TableCell align="center"><strong>Åtgärd</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.stuckAssets.map((asset) => (
                  <TableRow key={asset._id} hover>
                    <TableCell>
                      <Tooltip title={asset.title}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {asset.title}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={asset.fileName}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {decodeURIComponent(asset.fileName)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={asset.aiStatus} 
                        color="warning" 
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="caption">
                        {formatDate(asset.lastAiAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={asset.aiAttempts || 0} 
                        size="small"
                        color={asset.aiAttempts && asset.aiAttempts > 3 ? 'error' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Återköa">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleRetryAsset(asset._id)}
                          disabled={actionLoading === `retry-${asset._id}`}
                        >
                          {actionLoading === `retry-${asset._id}` ? (
                            <CircularProgress size={20} />
                          ) : (
                            <PlayArrowIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Pending Assets Table */}
      {details?.pendingAssets && details.pendingAssets.length > 0 && (
        <Paper>
          <Box sx={{ p: 2, bgcolor: '#e3f2fd' }}>
            <Typography variant="h6" sx={{ color: '#1976d2' }}>
              📋 Väntande Jobb ({details.pendingAssets.length})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Produkter som väntar på att processas (visar max 50 första).
            </Typography>
          </Box>
          <Divider />
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Titel</strong></TableCell>
                  <TableCell><strong>Filnamn</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {details.pendingAssets.map((asset) => (
                  <TableRow key={asset._id} hover>
                    <TableCell>
                      <Tooltip title={asset.title}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {asset.title}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={asset.fileName}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                          {decodeURIComponent(asset.fileName)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={asset.aiStatus} 
                        color="info" 
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* No data message */}
      {details && details.queue.queued === 0 && details.queue.running === 0 && 
       (!details.stuckAssets || details.stuckAssets.length === 0) && 
       (!details.pendingAssets || details.pendingAssets.length === 0) && (
        <Alert severity="info" sx={{ mt: 2 }}>
          AI-kön är tom. Inga jobb körs eller väntar.
        </Alert>
      )}
    </Box>
  );
};

export default AiQueueManagement;

